import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Alert, Button, DatePicker, Descriptions, Form, Input, Tag } from 'antd';
import moment from 'moment';
import labels from '../../../../constants/labels.json';
import { messages } from '../../../../constants';
import { formConfig } from '../../../../constants';
import {
  resetSantanderPaymentQuery,
  sendSantanderId,
} from '../../../../appRedux/actions/Sales';

const santanderDateFormat = 'DD/MM/YYYY';

const santanderStatus = {
  paid: { label: 'Pagado', color: 'green', alert: 'success' },
  failed: { label: 'Fallido', color: 'red', alert: 'error' },
  pending: { label: 'Pendiente', color: 'orange', alert: 'warning' },
  created: { label: 'Creado', color: 'blue', alert: 'info' },
  canceled: { label: 'Cancelado', color: 'red', alert: 'error' },
  expired: { label: 'Expirado', color: 'red', alert: 'error' },
};

const getSantanderStatus = (status, found) => {
  if (!found) {
    return { label: 'No encontrado', color: 'default', alert: 'warning' };
  }

  return (
    santanderStatus[status] || {
      label: status || 'Sin confirmar',
      color: 'default',
      alert: 'info',
    }
  );
};

const getTextValue = (value) => {
  if (value === null || value === undefined || value === '') {
    return '-';
  }

  if (typeof value === 'object') {
    return JSON.stringify(value);
  }

  return value;
};

export const SantanderModalBody = ({ isOpen }) => {
  const dispatch = useDispatch();
  const { santanderPaymentQuery: queryResult } = useSelector(
    (state) => state.sales
  );
  const [form] = Form.useForm();
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    form.resetFields();
    form.setFieldsValue({ date: moment() });
    setIsSubmitting(false);
    dispatch(resetSantanderPaymentQuery());
    // Run only when the modal opens; including the form instance can reset the
    // Santander result immediately after the successful request in this modal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, isOpen]);

  const handleSubmit = useCallback(
    async (values) => {
      setIsSubmitting(true);
      await dispatch(
        sendSantanderId({
          ...values,
          date: values.date?.format(santanderDateFormat),
        })
      );
      setIsSubmitting(false);
    },
    [dispatch]
  );

  const status = getSantanderStatus(queryResult?.status, queryResult?.found);
  const transaction = queryResult?.transaction || {};
  const operationNumber = transaction.nu_operacion || transaction.nu_operaion;

  return (
    <Form {...formConfig} onFinish={handleSubmit} form={form}>
      <Form.Item
        name="santanderId"
        label={labels.santander_id}
        rules={[
          { required: true, message: messages.required(labels.santander_id) },
        ]}
      >
        <Input />
      </Form.Item>
      <Form.Item
        name="date"
        label="Fecha"
        rules={[{ required: true, message: messages.required('Fecha') }]}
      >
        <DatePicker format={santanderDateFormat} style={{ width: '100%' }} />
      </Form.Item>
      <Form.Item>
        <Button
          type="primary"
          htmlType="submit"
          className="btn-form"
          loading={isSubmitting}
        >
          {labels.send}
        </Button>
      </Form.Item>
      {queryResult && (
        <Alert
          showIcon
          type={status.alert}
          message={
            <>
              Estado Santander: <Tag color={status.color}>{status.label}</Tag>
            </>
          }
          description={
            <Descriptions column={1} size="small">
              <Descriptions.Item label="Reference">
                {getTextValue(queryResult.reference)}
              </Descriptions.Item>
              <Descriptions.Item label="Pago local">
                {getTextValue(queryResult.paymentId)}
              </Descriptions.Item>
              <Descriptions.Item label="Orden">
                {getTextValue(queryResult.orderId)}
              </Descriptions.Item>
              <Descriptions.Item label="Operación Santander">
                {getTextValue(operationNumber)}
              </Descriptions.Item>
              <Descriptions.Item label="Respuesta Santander">
                {getTextValue(transaction.nb_response)}
              </Descriptions.Item>
              <Descriptions.Item label="Respuesta banco">
                {getTextValue(transaction.nb_resp)}
              </Descriptions.Item>
              <Descriptions.Item label="Autorización">
                {getTextValue(transaction.nu_auth)}
              </Descriptions.Item>
              <Descriptions.Item label="Importe">
                {getTextValue(
                  transaction.nu_importe
                    ? `${transaction.nu_importe} ${transaction.nb_currency || ''}`
                    : null
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Tarjeta">
                {getTextValue(
                  transaction.cc_num
                    ? `${transaction.cc_tp || 'Tarjeta'} ${transaction.cc_num}`
                    : null
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Tipo de pago">
                {getTextValue(transaction.cd_tipopago)}
              </Descriptions.Item>
              <Descriptions.Item label="Tipo de cobro">
                {getTextValue(transaction.cd_tipocobro)}
              </Descriptions.Item>
              <Descriptions.Item label="Instrumento">
                {getTextValue(transaction.cd_instrumento)}
              </Descriptions.Item>
              <Descriptions.Item label="Fecha Santander">
                {getTextValue(transaction.fh_registro)}
              </Descriptions.Item>
              {queryResult.message && (
                <Descriptions.Item label="Mensaje">
                  {getTextValue(queryResult.message)}
                </Descriptions.Item>
              )}
            </Descriptions>
          }
        />
      )}
    </Form>
  );
};
